import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Ctrl+K / Cmd+K command palette — global quick nav + universal symbol search.
 */
const COMMANDS = [
  { id: "nav-home",     label: "Command Center",       path: "/",            group: "Navigate", kbd: "G H" },
  { id: "nav-agents",   label: "30 AI Agents",         path: "/agents",      group: "Navigate", kbd: "G A" },
  { id: "nav-research", label: "AI Research",          path: "/research",    group: "Navigate", kbd: "G R" },
  { id: "nav-trades",   label: "Trade Ideas",          path: "/trade-ideas", group: "Navigate", kbd: "G T" },
  { id: "nav-chart",    label: "Live Chart",           path: "/chart",       group: "Navigate", kbd: "G C" },
  { id: "nav-options",  label: "Options Strategy Lab", path: "/options",     group: "Navigate" },
  { id: "nav-portfolio",label: "Portfolio Optimizer",  path: "/portfolio",   group: "Navigate" },
  { id: "nav-backtest", label: "Backtesting Engine",   path: "/backtest",    group: "Navigate" },
  { id: "nav-altdata",  label: "Alt-Data Terminal",    path: "/altdata",     group: "Navigate" },
  { id: "nav-memory",   label: "Memory & RAG",         path: "/memory",      group: "Navigate" },
  { id: "nav-brokers",  label: "Brokers",              path: "/brokers",     group: "Navigate" },
  { id: "nav-chat",     label: "AI Chat",              path: "/chat",        group: "Navigate" },
  { id: "nav-security", label: "Security & 2FA",       path: "/security",    group: "Navigate" },
  { id: "nav-credits",  label: "Credits & Billing",    path: "/credits",     group: "Navigate" },
  { id: "nav-admin",    label: "Admin Panel",          path: "/admin",       group: "Navigate" },
  { id: "act-idea-nvda",label: "Trade Idea → NVDA",    path: "/trade-ideas?s=NVDA", group: "Quick Action" },
  { id: "act-idea-btc", label: "Trade Idea → BTC-USD", path: "/trade-ideas?s=BTC-USD", group: "Quick Action" },
  { id: "act-idea-spy", label: "Trade Idea → SPY",     path: "/trade-ideas?s=SPY", group: "Quick Action" },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [symbolResults, setSymbolResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setOpen(v => !v); setQ(""); setIdx(0); setSymbolResults([]);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced universal symbol search — hits /api/market/symbols/search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const s = q.trim();
    if (s.length < 2) { setSymbolResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/market/symbols/search", { params: { q: s, limit: 6 } });
        setSymbolResults(data.items || []);
      } catch {
        setSymbolResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const staticFiltered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COMMANDS;
    return COMMANDS.filter(c => c.label.toLowerCase().includes(s) || c.group.toLowerCase().includes(s));
  }, [q]);

  // Merge: static commands first, then dynamic symbol matches
  const filtered = useMemo(() => {
    const symbolCmds = symbolResults.map(sr => ({
      id: `sym-${sr.symbol}`,
      label: `${sr.symbol} · ${sr.name || sr.type || ""}`.trim(),
      path: `/trade-ideas?s=${encodeURIComponent(sr.symbol)}`,
      group: "Symbols",
      meta: `${sr.exchange || ""} ${sr.type || ""}`.trim(),
    }));
    return [...staticFiltered, ...symbolCmds];
  }, [staticFiltered, symbolResults]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(c => { (g[c.group] = g[c.group] || []).push(c); });
    return g;
  }, [filtered]);

  const runCommand = (c) => {
    setOpen(false);
    navigate(c.path);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4" data-testid="command-palette" onClick={() => setOpen(false)}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }} />
      <div
        className="relative w-full max-w-2xl glass"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          data-testid="palette-input"
          autoFocus
          value={q}
          onChange={(e) => { setQ(e.target.value); setIdx(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setIdx(i => Math.min(filtered.length - 1, i + 1)); }
            if (e.key === "ArrowUp")   { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
            if (e.key === "Enter" && filtered[idx]) { e.preventDefault(); runCommand(filtered[idx]); }
          }}
          placeholder="Search symbols, agents, trades, or type a command…"
          className="w-full bg-transparent px-5 py-4 text-base text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none border-b"
          style={{ borderColor: "var(--border-default)" }}
        />
        <div className="max-h-96 overflow-y-auto py-2">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <div className="px-5 pt-3 pb-1 text-[10px] font-mono-data tracking-widest text-[var(--text-tertiary)]">
                {group.toUpperCase()}
                {group === "Symbols" && searching && <span className="ml-2 text-[color:var(--text-muted)]">searching…</span>}
              </div>
              {items.map((c) => {
                const flatIdx = filtered.indexOf(c);
                const active = flatIdx === idx;
                return (
                  <button
                    key={c.id}
                    data-testid={`palette-item-${c.id}`}
                    onClick={() => runCommand(c)}
                    onMouseEnter={() => setIdx(flatIdx)}
                    className={`w-full flex items-center justify-between px-5 py-2.5 text-left transition-colors ${active ? "bg-[var(--bg-elevated)]" : ""}`}
                  >
                    <span className="text-sm text-[var(--text-primary)] truncate">{c.label}</span>
                    {c.kbd && <span className="text-[10px] font-mono-data text-[var(--text-tertiary)]">{c.kbd}</span>}
                    {c.meta && !c.kbd && <span className="text-[10px] font-mono-data text-[var(--text-tertiary)]">{c.meta}</span>}
                  </button>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-5 py-6 text-center text-sm text-[var(--text-tertiary)]">
              {searching ? "Searching global markets…" : `No matches for "${q}"`}
            </div>
          )}
        </div>
        <div className="px-5 py-2 border-t text-[10px] font-mono-data text-[var(--text-tertiary)] flex justify-between" style={{ borderColor: "var(--border-default)" }}>
          <span>↑↓ navigate · ↵ run · esc close</span>
          <span>⌘K to reopen</span>
        </div>
      </div>
    </div>
  );
}
